import { ApiResponse, FilterMethod, ICustomFilter, ISortRequest } from '@enter/core';
import _ from 'lodash';
import { makeObservable, observable, runInAction } from 'mobx';
import { createRequestOptions, IFetchResourceRequest, parseFetchListRaw } from '../services/store/store.service';

const defaultFilters: ICustomFilter[] = [
    {
        filterId: 'filterActive',
        filterMethod: FilterMethod.EQ,
        filterType: 'Boolean',
        value: { label: 'Yes', value: '1' },
    },
];

export interface IResourceStore<T = any> {
    loading: boolean;
    limit: number;
    offset: number;
    total: number;
    resourceList: T[];

    fetch?: (req?: IFetchResourceRequest) => void;
    create?: (payload: any) => void;
    edit?: (id: string, payload: any) => void;
    delete?: (id: string) => Promise<void>;
}

type RequestOptions =
    | {
          limit: number | undefined;
          offset: number | undefined;
          sort: any[] | undefined;
          order: any[] | undefined;
      }
    | { [filterKey: string]: string };

export class ResourceStore<T> {
    @observable public loading: boolean = false;
    @observable public limit: number = 0;
    @observable public offset: number = 0;
    @observable public total: number = 0;
    @observable public resourceList: T[] = [];

    private req: IFetchResourceRequest = {};

    constructor() {
        // MobX 6 requires makeObservable to enable decorators
        makeObservable(this);
    }

    protected async handleFetchResource(raw: ApiResponse<T[]>): Promise<void> {
        const { value: resourceList, limit, offset, total } = await parseFetchListRaw<T>(raw);

        runInAction(() => {
            this.resourceList = resourceList;
            this.limit = limit;
            this.offset = offset;
            this.total = total;
        });
    }

    protected handleCreateRequestOptions(
        req: IFetchResourceRequest = this.req,
        sortEnum: any,
        orderEnum: any,
    ): RequestOptions {
        this.req = req;

        req.filters = this.setDefaultFilters(req.filters);
        req.sortRequest = this.setDefaultSort(req.sortRequest);

        return createRequestOptions(req, sortEnum, orderEnum);
    }

    private setDefaultSort(sortRequest: ISortRequest | undefined): ISortRequest | undefined {
        if (!sortRequest) {
            return sortRequest;
        }

        if (sortRequest.order.length === 0 && sortRequest.sort.length === 0) {
            return {
                sort: ['CREATED'],
                order: ['DESCENDING'],
            };
        }

        return sortRequest;
    }

    private setDefaultFilters(filters: ICustomFilter[] | undefined): ICustomFilter[] | undefined {
        const filtersWithDefaults = [...(filters || [])];
        defaultFilters.forEach(filter => {
            const isExists = _.find(filters, {
                filterId: filter.filterId,
            });

            if (!isExists) {
                filtersWithDefaults.push(filter);
            }
        });

        return filtersWithDefaults;
    }
}
